import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Footer } from '@/components/Footer/Footer';
import { PageTopControls } from '@/components/PageTopControls';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Button } from '@/components/UI/Button/Button';
import { HeartIcon, ShareIcon } from '@/components/UI/Icons/Icons';
import * as S from '@/views/Ads/Ad.styles';
import { MainInfo } from '@/components/Ad/MainInfo/MainInfo';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { Status } from '@/components/Ad/Status/Status';
import { Settings } from '@/components/Ad/Settings/Settings';
import { Properties } from '@/components/Ad/Properties/Properties';
import { BankPromo } from '@/components/Ad/BankPromo/BankPromo';
import { Description } from '@/components/Ad/Description/Description';
import { AuthorInfo } from '@/components/Ad/AuthorInfo/AuthorInfo';
import { Promotions } from '@/components/Ad/Promotions/Promotions';
import { Complaint } from '@/components/Ad/Complaint/Complaint';
import { Overview } from '@/components/Ad/Overview/Overview';
import { SliderWithGallery } from '@/components/Ad/SliderWithGallery/SliderWithGallery';
import { AdStatus } from '@/types';
import { ErrorView } from '@/views/Error/Error.view';
import { FloatingButtons } from '@/components/Ad/FloatingButtons/FloatingButtons';
import { useShareData } from '@/hooks/useShareData';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import {
  MAX_SHOP_RELATED_ADS,
  MAX_USER_RELATED_ADS,
} from '@/views/Ads/Ad.view.constants';
import { useToastStore } from '@/store/useToastStore';
import { useAdDetails } from '@/hooks/useAdDetails';
import { WarningPendingState } from '@/components/Ad/WarningPendingState/WarningPendingState';
import { ShopBanner } from '@/components/Ad/ShopBanner/ShopBanner';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useIsShop } from '@/hooks/useIsShop';
import { useBanner } from '@/hooks/useBanner';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { Toast } from '@/components/UI/Toast';
import { WebViewPaymentStatusModal } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal';
import { PaymentInTerminalsAfterAuth } from '@/components/Payment/PaymentInTerminalsAfterAuth';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Ad } from '@/api/types/common.types';
import { extractTargetParams } from '@/utils/helpers';
import { useServerDataContext } from '@/contexts';
import { useBookmark } from '@/hooks/useBookmark';
import { SuccessPaymentStatusModal } from '@/components/Payment/SuccessPaymentStatusModal';
import { Header } from '@/components/Header';
import { getCurrentCategoryPath } from '@/hooks/useCurrentCategory/useCurrentCategory.helpers';
import { buildBreadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs.helpers';
import { CollectionMasterSheet } from '@/features/BookmarkCollections/components';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import NoSsr from '@/components/NoSsr/NoSsr';
import { RelatedAds } from '@/components/Ad/RelatedAds/RelatedAds';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';

export const AdView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const customPush = useCustomPush();
  const { adDetails, loading } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const shop = adData?.shop;
  const [isGalleryOpen, toggleGallery] = useToggleOpenState();

  const targetParams = useMemo(
    () => extractTargetParams(adData ?? undefined),
    [adData]
  );

  const { topBanner } = useBanner({
    targetParams: targetParams || {},
    canLoadTopBanner: !loading && !!targetParams,
  });

  const isAuthor = useIsAuthor();
  const isShop = useIsShop(adData);

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);

  const { openToast, closeToast } = useToastStore();
  const { categoryDetails } = useCurrentCategoryDetails();

  const status = adData?.status;
  const { shareUrl, canShare } = useShareData();
  const { isBookmarked: isFavorite, toggleBookmark } = useBookmark({
    ad: adData ?? undefined,
  });

  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

  useEffect(() => {
    closeToast();
  }, [isGalleryOpen]);

  function showMore() {
    const moreAdsLink = isShop ? shop?.uri : `/${categoryDetails?.path}`;
    if (moreAdsLink) {
      customPush(moreAdsLink);
    }
  }

  function handleBookmarkClick() {
    toggleBookmark();

    if (!isFavorite) {
      handleAddToCollection();
    } else {
      openToast({
        isLight: isGalleryOpen,
        text: t('bookmark_collections.unbookmarked'),
      });
    }
  }

  function handleAddToCollection() {
    openToast({
      isLight: isGalleryOpen,
      text: t('bookmark_collections.toast_tip'),
      action: t('bookmark_collections.toast_action'),
      onActionClick: handleToastActionClick,
    });
  }

  function handleToastActionClick() {
    closeToast();
    toggleGallery(false);
    setIsCollectionMasterOpen(true);
  }

  const { Published, Expired } = AdStatus;
  const isExpiredOrPublished = status && [Published, Expired].includes(status);
  const canComplain =
    !isAuthor && isExpiredOrPublished && !userInfo?.currentUser?.isBanned;
  const canBookmark = status === Published || (status === Expired && isAuthor);
  const shouldShowNotFound = !adData || (!isAuthor && !isExpiredOrPublished);
  const userRoleCondition = isAuthor
    ? isExpiredOrPublished
    : status === Published;
  const isShareVisible = canShare && userRoleCondition;

  // TODO: set_edit_limit_reached param needed only for rails ad page. Remove this after implementing responsive ad page
  useEffect(() => {
    if (router.query.set_edit_limit_reached) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars,camelcase
      const { set_edit_limit_reached, ...cleanQuery } = router.query;
      const currentHash = window.location.hash;
      router.replace({ query: cleanQuery, hash: currentHash }, undefined, {
        shallow: true,
      });
    }
  }, [router]);

  function getHeaderActions() {
    if (loading) {
      return (
        <S.AdHeaderActions>
          <SkeletonLoader width="17px" height="20px" />
          <SkeletonLoader width="20px" height="20px" />
        </S.AdHeaderActions>
      );
    }

    return (
      <S.AdHeaderActions>
        {isShareVisible && (
          <Button
            appearance="transparent"
            hasPadding={false}
            onClick={shareUrl}
            aria-label="Share"
          >
            <ShareIcon
              fill={theme.palette.primary.labels}
              className="share-btn js-share-btn"
            />
          </Button>
        )}

        {canBookmark && (
          <Button
            appearance="transparent"
            aria-label="Toggle bookmark"
            hasPadding={false}
            onClick={handleBookmarkClick}
            data-stat={
              !isFavorite && isShop ? 'shop-ad-bookmarking' : undefined
            }
            className={!isFavorite ? 'add_bookmark' : undefined}
          >
            <HeartIcon
              data-testid={AdPageTestIds.headerActions.bookmarkIcon}
              fill={isFavorite ? theme.palette.brand.main : 'none'}
              stroke={
                isFavorite
                  ? theme.palette.brand.main
                  : theme.palette.primary.labels
              }
            />
          </Button>
        )}
      </S.AdHeaderActions>
    );
  }

  if (!loading && shouldShowNotFound) {
    return (
      <ErrorView
        title={t('pages.ad.nothing_found')}
        subtitle={t('pages.ad.nothing_found_ad_description')}
        hasTopBanner
      />
    );
  }

  const adsLimit = isShop ? MAX_SHOP_RELATED_ADS : MAX_USER_RELATED_ADS;
  const adsList = adData
    ? adData.relatedAds.slice(0, adsLimit)
    : Array.from({ length: MAX_USER_RELATED_ADS }, () => ({} as Ad));

  const hasShowMoreButton = isShop
    ? status &&
      ![
        AdStatus.Rejected,
        AdStatus.Expired,
        AdStatus.AwaitingPayment,
        AdStatus.Archived,
      ].includes(status)
    : (adsList?.length ?? 0) >= adsLimit;
  const shouldShowBanner = !isShop && status !== AdStatus.Pending;
  const hasFloatingButtons = !isAuthor && status === AdStatus.Published;
  const shouldShowBurgerInHeader = status === AdStatus.Pending;

  return (
    <>
      <NoSsr>{shouldShowBanner && topBanner}</NoSsr>

      <PageTopControls>
        <Header
          isServerRendered={false}
          hasBurger={shouldShowBurgerInHeader}
          rightAction={getHeaderActions()}
        />
      </PageTopControls>

      <Breadcrumbs
        hasBg
        hasHorizontalPadding
        breadcrumbs={buildBreadcrumbs(categoryDetails)}
        nestingLvl={getCurrentCategoryPath(router.query.slug).split('/').length}
      />

      {isAuthor && status === AdStatus.Pending && <WarningPendingState />}

      {isShop && <ShopBanner shop={shop} />}

      <SliderWithGallery
        isGalleryOpen={isGalleryOpen}
        toggleGallery={toggleGallery}
        isBookmarked={isFavorite}
        toggleBookmark={handleBookmarkClick}
        canBookmark={canBookmark}
        {...(status === AdStatus.Expired &&
          !isAuthor && { overlayText: t('common.ad_inactive') })}
      />

      {status && <Status status={status} isAuthor={isAuthor} />}

      <S.AdViewMain>
        {isAuthor ? (
          <>
            <MainInfo />
            <Promotions />
            <Settings />
            <Properties />
            <BankPromo />
            <Description />
            <AuthorInfo />
            <Overview />
          </>
        ) : (
          <>
            <MainInfo />
            <Properties />
            <BankPromo />
            <Description />
            <AuthorInfo />
            <Overview />
            <Divider $isSmall />
            {canComplain && <Complaint />}
            <Promotions hasBottomMargin />
          </>
        )}
      </S.AdViewMain>

      <CollectionMasterSheet
        isOpen={isCollectionMasterOpen}
        onClose={() => setIsCollectionMasterOpen(false)}
      />

      {adsList.length > 0 ? null : <Toast />}

      <NoSsr>
        <RelatedAds
          ads={adsList}
          isLoading={loading}
          chunkSize={adsLimit}
          title={
            adsList?.length
              ? t(`pages.ad.${isShop ? 'other_users_ads' : 'similar_ads'}`)
              : undefined
          }
          onShowMore={showMore}
          hasBottomButton={hasShowMoreButton}
          {...(isShop && {
            showMoreText: t('pages.ad.go_to_shop'),
          })}
          transitionSource={
            isShop
              ? TransitionSourceBlockEnum.OtherUserAds
              : TransitionSourceBlockEnum.SimilarAds
          }
        />
      </NoSsr>

      {hasFloatingButtons && <FloatingButtons />}

      <Footer {...(hasFloatingButtons && { bottomGutter: '100px' })} />

      <PaymentInTerminalsAfterAuth adId={adData?.id} />

      <WebViewPaymentStatusModal />

      <SuccessPaymentStatusModal />
    </>
  );
};
